import { CancellationToken } from '../../hooks/useEffectAsync';
import useStateRef from '../../hooks/useStateRef';
import { useCallback, useEffect } from 'react';
import { SessionResponse } from '../../models/ChatTypes';
import { useChat } from '../chat/ChatContext';
import { SessionEvent, SessionEventTypesPrefixes } from '../../models/BackendEvents';


const useChatsHistory = () => {
    const { getAllChats, events: { session: sessionEvents } } = useChat();
    const [chatsHistory, setChatsHistory, chatsHistoryRef] = useStateRef<SessionResponse[]>();

    const loadChatsHistory = useCallback((async ({ token, force }: { token?: CancellationToken, force?: boolean; }) => {
        if (chatsHistory?.length && !force) return;

        try {
            if (token?.cancelled) return;
            const chats = await getAllChats();

            setChatsHistory([...chats]);
        } catch (e) {
            if (token?.cancelled) return;
            console.error({ e });
        }
    }), [chatsHistory, getAllChats, setChatsHistory]);

    const reloadChatsHistory = useCallback(async (force?: boolean) => {
        // only reload force is true or chat history has not been loaded
        if (force || !chatsHistoryRef.current)
            await loadChatsHistory({ force });
        return Promise.resolve<void>(undefined);
    }, [chatsHistoryRef, loadChatsHistory]);

    const onSessionEvent = useCallback((model: SessionEvent) => {
        // Handling workflow updates
        // TODO: handle name updates.
        if (!model.type.startsWith(SessionEventTypesPrefixes.workflow)) {
            return;
        }
        const targetIndex = chatsHistoryRef.current?.findIndex(x => x.session_id === model.session_id) ?? -1;

        if (targetIndex === -1) {
            return;
        }
        const target = chatsHistoryRef.current![targetIndex];
        const copy = [...chatsHistoryRef.current!];
        copy[targetIndex] = { ...target, workflow_status: model.type.replace(SessionEventTypesPrefixes.workflow, "") as any };
        setChatsHistory(copy);
    }, [chatsHistoryRef, setChatsHistory]);

    useEffect(() => {
        const events = sessionEvents;
        events.on(onSessionEvent);
        return () => events.off(onSessionEvent);
    }, [sessionEvents, onSessionEvent]);

    return {
        chatHistory: {
            items: chatsHistory,
            set: setChatsHistory,
            reload: reloadChatsHistory
        }
    };
};

export default useChatsHistory;